<template>
    <div>
        <ui-modal
            v-if="isOpen"
            ref="editModal"
            size="xlarge"
            :class="{ 'expense-credit-note': isCreditNote }"
            :title="title"
            @close="$emit('close')"
            @hide="hide()"
            dismiss-on="close-button esc"
        >
            <div style="display: flex;">
                <div v-if="hasDocuments" style="flex-grow: 1; max-width: 40%; padding: 20px 70px 0px 30px;">
                    <img v-for="document in expense.documents" :src="document.preview_url" style="max-width: 100%; box-shadow: 5px 5px 10px 5px rgba(0, 0, 0, 0.25); margin-bottom: 30px;">
                </div>
                <div :style="{ flexGrow: 1, maxWidth: (hasDocuments ? '60%' : '100%') }">
            <div v-if="!isLoading" class="container-fluid" style="padding: 0; margin-top: 1rem;">
                <xero-sync-error-alert :owner="expense" style="margin-top: -1rem;"></xero-sync-error-alert>
                <div class="row">
                    <div class="col-2">
                        <ui-datepicker
                            :start-of-week="1"
                            v-model="expense.tax_at"
                            :custom-formatter="dateFormatter"
                            append-dropdown-to-body
                        >Tax Date</ui-datepicker>
                    </div>
                    <div class="col-2">
                        <team-picker 
                            v-model="teamProxy"
                            label="Team" 
                            :disabled="team && disableTeamSelectionOnValidTeam ? true : false"
                            @loaded="options => { (1 === options.length) ? teamProxy = options[0] : null }"
                        />
                    </div>
                    <div class="col-2">
                        <ui-textbox
                            v-model="expense.reference"
                            label="Supplier Ref #"
                        >
                        </ui-textbox>
                    </div>
                    <div class="col-3 offset-3 text-right">
                        <payment-status
                            v-if="expense.id && bankTransactionsEnabled && expense.status === ExpenseStatus.Authorized"
                            :amount-paid="expense.amount_paid ?? 0"
                            :total-amount="expense.gross ?? 0"
                            :is-credit="expense.is_credit"
                            @add-payment="expense.is_credit ? showApplyCreditNote() : showPayAndCredit()"
                        ></payment-status>
                    </div>
                </div>
                <div class="row align-items-center">
                    <div class="col-6">
                        <contact-picker
                            label="Supplier"
                            v-model="contactProxy"
                            :new-contact-defaults="newContactDefaults"
                            show-account-code
                        ></contact-picker>
                    </div>
                    <div class="col offset-1 text-right">

                        <ui-button
                            v-if="!bankTransactionsEnabled"
                            @click="updatePaymentStatus(expense)"
                            :color="expense.is_paid ? 'green' : 'default'"
                            :icon="paymentButtonIcon"
                            type="secondary"
                            size="large"
                        >{{ paymentButtonText }}</ui-button>

                        <ui-button
                            @click="updateDocuments"
                            color="default"
                            icon="attachment"
                            type="secondary"
                            size="large"
                        >
                            Files
                            <span class="badge badge-danger ml-2">{{ documentCount }}</span>
                        </ui-button>

                        <ui-icon-button
                            @click="hasFlag = !hasFlag"
                            :color="hasFlag ? 'red' : 'default'"
                            icon="flag"
                            type="secondary"
                            style="vertical-align: middle;"
                            size="large"
                        ></ui-icon-button>
                    </div>
                </div>

                <hr>

                <!-- Items !-->
                <div class="row" style="margin-top: 1.5rem;">
                    <div class="col-12">
                        <div class="row" style="margin-bottom: 1rem;">
                            <div class="col">
                                <h5>Items</h5>
                            </div>
                            <div class="col text-right">
                                <ui-button size="small" color="primary" icon="add" @click="addItem">Add</ui-button>
                            </div>
                        </div>
                    </div>
                    <div class="col-12">
                        <div class="table-responsive">
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>Description</th>
                                        <th style="width: 200px;">Category</th>
                                        <th style="width: 120px;">VRM</th>
                                        <th style="width: 60px;">Qty</th>
                                        <th style="width: 110px;">Unit</th>
                                        <th style="width: 110px;">VAT</th>
                                        <th style="width: 110px;">Total</th>
                                        <th style="width: 60px;"></th>
                                    </tr>
                                </thead>
                                <tbody v-if="expenseItems.length > 0">
                                    <tr v-for="(item, index) in expense.items" :key="index" class="col-12" v-show="!item.is_deleted">
                                        <td>
                                            <div>{{ item.description }}<span v-if="item.id" style="color: #ccc;"> - #{{ item.id }}</span></div>
                                        </td>
                                        <td>{{ item.expense_category.name }}</td>
                                        <td><span @click="viewStockListing(item.instance)" style="cursor: pointer;" class="registration small" v-if="item.instance && item.instance.id">{{ item.instance.vehicle.registration ? item.instance.vehicle.registration.formatted_vrm : 'No Reg' }}</span><span v-else>-</span></td>
                                        <td>{{ item.quantity }}</td>
                                        <td>{{ item.unit_cost | currency(2) }}</td>
                                        <td>{{ item.vat_rate }}</td>
                                        <td>
                                            {{ getInvoiceHelper().itemTotal(index) | currency(2) }}
                                        </td>
                                        <td class="text-right" style="padding-top: .5rem; padding-bottom: .5rem;">
                                            <ui-icon-button v-if="!item.part_exchange_record" has-dropdown icon="more_vert" :ref="'dropdownButton' + index">
                                                <ui-menu
                                                    @select="e => { costMenuSelected(e, item, index) }"
                                                    contain-focus
                                                    has-icons
                                                    slot="dropdown"
                                                    :options="getMenuOptions(item)"
                                                    @close="$refs['dropdownButton' + index][0].closeDropdown()"
                                                ></ui-menu>
                                            </ui-icon-button>
                                        </td>
                                    </tr>
                                </tbody>
                                <tbody v-else>
                                    <tr>
                                        <td colspan="8">No items</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-6"></div>

                    <!-- Totals !-->
                    <div class="col-5 offset-1">

                    <!-- Subtotal !-->
                    <div class="row align-items-center" style="margin-top: 1.5rem; font-size: 1.2rem;">
                        <div class="col"></div>
                        <div class="col text-right">
                            Subtotal
                        </div>
                        <div class="col text-right" style="max-width: 180px;">
                            {{ getInvoiceHelper().net() | currency(2) }}
                        </div>
                    </div>

                     <!-- VAT !-->
                    <div class="row align-items-center" style="font-size: 1.2rem; margin-top: .5rem;">
                        <div class="col text-right" style="font-size: .8rem;">
                            VAT @ {{ getInvoiceHelper().vatRate() }}% on {{ getInvoiceHelper().netVatable() | currency(2) }}
                        </div>
                        <div class="col text-right" style="max-width: 180px;">
                            {{ getInvoiceHelper().vat() | currency(2) }}
                        </div>
                    </div>

                    <!-- Total !-->
                    <div class="row align-items-center" style="font-size: 1.2rem; margin-top: .5rem;">
                        <div class="col text-right">
                            Total
                        </div>
                        <div class="col text-right" style="max-width: 180px;">
                            {{ getInvoiceHelper().total() | currency(2) }}
                        </div>
                    </div>

                    <template v-if="bankTransactionsEnabled && expense.payments && (expense.credits || expense.allocations)">
                    <!-- Payments & Credits !-->
                    <div v-if="expense.payments.length > 0 || expense.credits.length > 0 || expense.allocations.length > 0"  class="row align-items-center" style="margin-top: 1rem;">
                        <div class="col-12 text-right" style=" border-top: 1px dashed #ccc; margin-bottom: 1rem;"></div>
                    </div>

                    <div v-for="payment in expense.payments"  class="row align-items-center expense-modal-payment-row">
                        <div class="col text-right">
                            <div>Less <a href="#" @click.prevent="showBankTransaction(payment.transaction.id)">Payment</a></div>
                            <div class="subtle">{{ payment.transaction.date | formatDate('DD MMM YYYY') }}</div>
                        </div>
                        <div class="col text-right" style="max-width: 180px;">
                            {{ payment.amount | abs | currency(2) }}
                        </div>
                    </div>
                    <div v-for="credit in expense.credits"  class="row align-items-center expense-modal-payment-row">
                        <div class="col text-right">
                            <div>Less <a href="#" @click.prevent="showApplyCreditNotes">Credit Note</a></div>
                            <div class="subtle">{{ credit.created_at | formatDate('DD MMM YYYY') }}</div>
                        </div>
                        <div class="col text-right" style="max-width: 180px; ">
                            {{ credit.amount | abs | currency(2) }}
                        </div>
                    </div>
                    <div v-for="allocation in expense.allocations"  class="row align-items-center expense-modal-payment-row">
                        <div class="col text-right">
                            Less Credit to <a href="#" @click="showApplyCreditNote">Expense {{ allocation.expense_id }}</a>
                            <div class="subtle">{{ allocation.created_at | formatDate('DD MMM YYYY') }}</div>
                        </div>
                        <div class="col text-right" style="max-width: 180px; ">
                            {{ allocation.amount | abs | currency(2) }}
                        </div>
                    </div>

                    <!-- Balance Due !-->
                    <div class="row align-items-center" style="font-size: 1.4rem; margin-top: 1rem; font-weight: 600;">
                        <div class="col text-right" style=" border-top: 1px dashed #ccc; padding-top: 1rem">
                            {{ expense.is_credit ? 'Available Balance' : 'Balance Due'}}
                        </div>
                        <div class="col text-right" style="max-width: 180px; border-top: 1px dashed #ccc; padding-top: 1rem">
                            {{ Math.abs(expense.amount_outstanding) | currency(2) }}
                        </div>
                    </div>
                    </template>
                </div>
                </div>
            </div>
                    </div>
            </div>

            <div v-if="!isLoading" slot="footer" style="width: 100%; text-align: right;">
                <ui-button v-if="expense.id > 0" icon="more_horiz" has-dropdown ref="expenseMenuButton" style="float: left;">
                    <ui-menu
                        contain-focus
                        has-icons
                        slot="dropdown"
                        :options="mainMenuOptions"
                        @select="mainMenuItemSelected"
                        @close="$refs.expenseMenuButton.closeDropdown()"
                    ></ui-menu>
                    More
                </ui-button>
                <view-on-xero-button :owner="expense" style="vertical-align: middle; float: left;"></view-on-xero-button>
                <ui-button type="secondary" color="primary" @click="save(true)">Save for Review</ui-button>
                <ui-button type="primary" color="primary" @click="save()">{{ expense.is_approved ? 'Save' : 'Save & Approve' }}</ui-button>
            </div>

            <div v-if="isLoading" class="container-fluid" style="padding: 4rem;">
                <ui-progress-circular style="margin:auto;" :size="48" :stroke="4"></ui-progress-circular>
            </div>
        </ui-modal>

        <!-- Expense Item Modal !-->
        <expense-item-modal
            ref="itemModal"
            :instance="this.instance"
            @save="handleItemChanged"
            @hide="$refs['editModal'].open();"
        ></expense-item-modal>

        <!-- Expense Paid At !-->
        <expense-payment-date-modal
            ref="paymentDate"
            @selected="updatePaidDate"
        ></expense-payment-date-modal>

        <!-- Document Management Modal !-->
        <document-management-modal
            ref="attachedFiles"
            :model-type="DocumentOwnerType.AccountingExpense"
            :model-id="expense.id ?? ''"
            :accept="AcceptedDocumentMimes"
            default-type="document"
            :existing-documents="expense.documents"
            :descriptions="[DocumentTypes.TYPE_INSTANCE_DOCUMENT]"
            :should-associate="!!expense.id"
            @saved="documentsSaved"
            title="Attached Files"
        />

        <!-- Apply Credits Modal !-->
        <apply-expense-credit-note-modal
            ref="applyCreditModal"
            @allocated="creditAllocated"
        ></apply-expense-credit-note-modal>
    </div>
</template>

<style lang="scss">
    .ui-modal.expense-credit-note {
        .ui-modal__body, .ui-modal__footer {
            background: #f9f5ec!important;
        }
    }
</style>
<style lang="scss" scoped>
    .expense-modal-payment-row:first-child {
        margin-top: 1rem;
    }

    .expense-modal-payment-row:last-child {
        margin-bottom: 1rem;
    }

    table tbody tr:last-child td { border-bottom: 1px solid #dee2e6; }
</style>

<script>
    import ContactPicker from './controls/KeenContactPicker'
    import ExpenseItemModal from './ExpenseItemModal'
    import DealerKit from "../../api";
    import TeamPicker from './controls/KeenTeamPicker'
    import { ExpenseSchema, ExpenseResource } from '../../classes/Api/Expense';
    import { ContactTypes } from '../../classes/Api/Contact';
    import { InvoiceHelper } from '../../classes/Api/Invoice';
    import Moment from 'moment'
    import Filters from '../../filters'
    import {goTo} from "../../utils";
    import ExpenseFlagHelpers from "../money/ExpenseFlagHelpers";
    import ExpensePaymentDateModal from "./ExpensePaymentDateModal.vue";
    import XeroSyncErrorAlert from "../../../ts/Accounts/XeroSyncErrorAlert.vue";
    import ViewOnXeroButton from "../../../ts/Accounts/ViewOnXeroButton.vue";
    import DocumentManagementModal from "../elements/DocumentManagementModal.vue";
    import {DocumentTypes} from "../../classes/Api/Document";
    import {DocumentOwnerType, ExpenseStatus} from "../../../../api-clients/ts";
    import PaymentStatus from "../../../ts/Accounts/PaymentStatus.vue";
    import Bus from "../../events";
    import ApplyExpenseCreditNoteModal from "../../../ts/Accounts/ApplyExpenseCreditNoteModal.vue";
    import button from "bootstrap/js/src/button";
    import {bankTransactionsEnabledGetter} from "../../../ts/Accounts/FeatureFlags";
    import {cloneDeep} from "lodash";
    import {AcceptedDocumentMimes} from "../../../ts/DocumentPaneMimes";

    export default {
        mixins: [ExpenseFlagHelpers],
        filters: Filters,
        components: {
            ApplyExpenseCreditNoteModal,
            PaymentStatus,
            DocumentManagementModal,
            ViewOnXeroButton,
            XeroSyncErrorAlert,
            ExpensePaymentDateModal,
            ContactPicker, 
            ExpenseItemModal,
            TeamPicker
        },
        props: {
            instance: {
                type: Object,
            },
            customButtonLabel: {
                type: String,
            },
            team: {
                type: Object,
                required: false,
                default: null
            },
            disableTeamSelectionOnValidTeam: {
                type: Boolean,
                required: false,
                default: true
            }
        },
        data() {
            return {
                expense: new ExpenseSchema({
                    tax_at: new Date(),
                    items: [],
                }),
                editingIndex: null,
                isLoading: false,
                ContactTypes,
                isOpen: false,
                hasFlag: false,
                documentsToAssociate: [],
                AcceptedDocumentMimes,
                ExpenseStatus,
            }
        },
        computed: {
            hasDocuments() {
                return this.expense.documents.length > 0
            },
            bankTransactionsEnabled: bankTransactionsEnabledGetter,
            button() {
                return button
            },
            documentCount() {
                let count = this.expense.documents ? this.expense.documents.length : 0

                if (this.documentsToAssociate.length > 0) {
                    count += this.documentsToAssociate.length;
                }

                return count
            },
            DocumentOwnerType() {
                return DocumentOwnerType
            },
            DocumentTypes() {
                return DocumentTypes
            },
            isCreditNote() {
                return this.expense.is_credit
            },
            title() {
                const type = this.isCreditNote ? 'Credit Note' : 'Expense'
                return (this.expense.id ? `Edit ${type} #${this.expense.id}` : `Add ${type}`)
            },
            newContactDefaults() {
                return {
                    type: ContactTypes.SUPPLIER,
                    referral_source: window.marque.ui.otherReferralSource
                }
            },
            expenseItems() {
                return this.expense.items.filter(item => { return !item.is_deleted })
            },
            buttonLabel() {
                if (this.customButtonLabel) {
                    return this.customButtonLabel;
                }

                return 'Save';
            },
            contactProxy: {
                get() {
                    return this.expense.contact;
                },
                set(value) {
                    this.$set(this.expense, 'contact', value);
                    this.$set(this.expense, 'contact_id', value.id);
                }
            },
            teamProxy: {
                get() {
                    return this.expense.team ? this.expense.team : {};
                },
                set(value) {
                    this.$set(this.expense, 'team', value);
                    this.$set(this.expense, 'team_id', value.id);
                }
            },
            mainMenuOptions() {
                return [
                {
                    id: 'accounting.provider',
                    label: 'View on Sage',
                    icon: 'link',
                    disabled: !this.expense.sync_record || !this.expense.sync_record.related_url,
                },
                {
                    type: 'divider'
                },
                {
                    id: 'delete',
                    label: 'Delete',
                    icon: 'delete',
                    disabled: !this.expense.id
                }]
            },
            paymentButtonText() {
                if (this.expense.paid_at) {
                    return 'Paid - ' + Moment(this.expense.paid_at).format('D/M/Y')
                }

                return 'Unpaid'
            },
            paymentButtonIcon() {
                return this.expense.paid_at ? 'check' : 'attach_money'
            }
        },
        methods: {
            hide() {
                this.expense = this.newExpense({})
                this.isOpen = false
            },
            showApplyCreditNotes() {
                this.$refs.applyCreditModal.applyCreditNotesToExpense(this.expense.id)
            },
            showApplyCreditNote() {
                this.$refs.applyCreditModal.applyCreditNoteToExpenses(this.expense.id)
            },
            showBankTransaction(id) {
                Bus.emit('showBankTransaction', { bank_transaction_id: id, onDestroyed: () => {
                    this.load(this.expense)
                    this.$emit('saved', this.expense)
                }})
            },
            showPayAndCredit() {
                Bus.emit('payExpenses', {
                    contact: this.expense.contact,
                    preselectExpenseIds: [this.expense.id],
                    restrictExpensesToContact: true,
                    onAllocated: () => {
                        this.load(this.expense)
                        this.$emit('saved', this.expense)
                    }
                })
            },
            documentsSaved({associated, toAssociate, toRemove}) {
                if (associated) {
                    this.load(this.expense)
                } else {
                    this.documentsToAssociate = toAssociate
                }
            },
            updateDocuments() {
                this.$refs.attachedFiles.open()
            },
            updatePaymentStatus(expense) {
                if (!expense.is_paid) {
                   this.$refs.paymentDate.open()
                } else {
                    expense.is_paid = false
                    expense.paid_at = null
                }
            },
            updatePaidDate({paid_at}) {
                this.expense.is_paid = true
                this.$set(this.expense, 'paid_at', paid_at)
            },
            viewStockListing(instance) {
                  goTo('/vehicles/' + instance.id + '/overview', true)
            },
            getInvoiceHelper() {
                return new InvoiceHelper(this.expense);
            },
            open(expense = null) {
                this.isOpen = true
                this.hasFlag = false
                this.documentsToAssociate = []

                this.$nextTick(() => {
                    if (expense && expense.id) {
                        this.load(expense)
                    } else {
                        this.expense = this.newExpense(expense)
                    }

                    this.$refs['editModal'].open();
                })
            },
            close() {
                this.$refs['editModal'].close();
            },
            newExpense(expense) {
                let attr = {
                    tax_at: new Date(),
                    is_credit: false,
                    items: [],
                    team: this.team,
                    team_id: this.team ? this.team.id : null,
                    contact: {},
                    documents: []
                }

                if (expense) {
                    attr = { ...attr, ...expense }
                }

                attr.items = this.absoluteItemValues(attr)

                return new ExpenseSchema(attr)
            },
            destroy() {
                this.isLoading = true;

                if (confirm('Are you sure you want to delete this expense?')) {
                    ExpenseResource.destroy(this.expense.id).then(response => {
                        this.$emit('destroyed', response.data);
                        this.isLoading = false;

                        this.$refs['editModal'].close();
                    }, error => {
                         console.log('Whoooh there, problems!', error);
                    });
                }
            },
            creditAllocated() {
                this.reload()

                this.$emit('saved', this.expense)
            },
            reload() {
                this.load(this.expense)
            },
            load(expense) {
                let relations = [
                    'contact',
                    'team',
                    'items',
                    'items.expenseCategory',
                    'items.instance',
                    'items.instance.location',
                    'items.tasks',
                    'items.partExchangeRecord',
                    'tags',
                    'xeroReferences',
                    'documents',
                    'payments',
                    'payments.transaction',
                    'credits',
                    'allocations',
                ]

                this.isLoading = true;

                ExpenseResource.find(expense.id, { with: relations }).then(response => {
                    this.expense = response.data

                    if (typeof expense.tax_at === 'string') {
                        expense.tax_at = (new Moment(expense.tax_at)).toDate();
                    }

                    this.expense.items = this.absoluteItemValues(this.expense)

                    for (let i in this.expense.items) {
                        this.$set(this.expense.items[i], 'is_deleted', false)
                    }

                    this.hasFlag = this.isFlagged(this.expense)
                }).catch((e) => {
                    handleApiError(e);
                    this.close()
                }).finally(() => this.isLoading = false);
            },
            absoluteItemValues(attr) {
                if (attr.is_credit) {
                    attr.net = Math.abs(attr.net)
                    attr.gross = Math.abs(attr.gross)
                    attr.vat = Math.abs(attr.vat)

                    attr.items = attr.items.map(item => {
                        return {
                            ...item,
                            unit_cost: Math.abs(item.unit_cost),
                            unit_net: Math.abs(item.unit_net),
                            unit_vat: Math.abs(item.unit_vat),
                            unit_gross: Math.abs(item.unit_gross),
                            custom_total_vat: Math.abs(item.custom_total_vat),
                            total_net: Math.abs(item.total_net),
                            total_vat: Math.abs(item.total_vat),
                            total_gross: Math.abs(item.total_gross),
                        }
                    })
                }

                console.log(attr)

                return attr.items
            },
            save(forReview = false) {
                this.isLoading = true;

                let expense = Object.assign({}, this.expense);
                let items = [];

                for (let i of this.expenseItems) {
                    const item = cloneDeep(i)
                    item.expense = null;
                    item.instance = null;

                    if (expense.is_credit) {
                        item.unit_cost = 0 - item.unit_cost
                        item.custom_total_vat = 0 - item.custom_total_vat
                    }

                    items.push(item)
                }

                expense.items = items;

                if (forReview) {
                    expense.status = ExpenseStatus.ForReview
                } else {
                    expense.status = ExpenseStatus.Authorized
                }

                let params = { with: ['items.expenseCategory', 'contact', 'items.instance'] };

                ExpenseResource.storeOrUpdate(this.expense.id, expense, params).then(response => {
                    let promises = []

                    if (this.hasFlag && !this.isFlagged(this.expense)) {
                        promises.push(ExpenseResource.tag(response.data.id, this.expenseFlagName))
                    } else if (!this.hasFlag && this.isFlagged(this.expense)) {
                        promises.push(ExpenseResource.untag(response.data.id, this.getFlagTag(this.expense).id))
                    }

                    if (this.documentsToAssociate.length > 0) {
                        promises.push(DealerKit.documents.associateDocuments({
                            owner_id: response.data.id,
                            owner_type: 'accounting.expense',
                            documents: this.documentsToAssociate.map(document => {
                                return {
                                    type: document.type,
                                    description: document.description,
                                    order: document.order,
                                    id: document.id
                                }
                            })
                        }))
                    }

                    Promise.all(promises).then(() => {
                        this.$emit('saved', response.data);

                        this.isLoading = false;

                        this.documentsToAssociate = []

                        this.$refs['editModal'].close();
                    })
                }, e => { this.isLoading = false; handleApiError(e) });
            },
            dateFormatter(date) {
                return (new Moment(date)).format('DD/MM/YYYY')
            },
            addItem() {
                this.editingIndex = null;

                this.$refs['itemModal'].open()
            },
            handleItemChanged(item) {
                let index = this.editingIndex;

                if (null === index) {
                    index = this.expense.items.length;
                }

                this.$set(this.expense.items, index, item);
            },
            costMenuSelected(item, expense_item, index) {
                switch (item.id) {
                    case 'delete':
                        this.expense.items[index].is_deleted = true
                    break;
                    case 'duplicate':
                        this.editingIndex = null;
                        const duplicate = cloneDeep(expense_item)
                        duplicate.id = null
                        this.handleItemChanged(duplicate);
                    break;
                    case 'edit':
                        let editItem = Object.assign({}, expense_item);
                        
                        this.editingIndex = index; 

                        this.$refs['itemModal'].open(editItem)
                    break;
                }
            },
            getMenuOptions(expense_items) {
                return [
                        {
                            id: 'edit',
                            icon: 'edit',
                            label: 'Edit',
                        },{
                            id: 'duplicate',
                            icon: 'content_copy',
                            label: 'Duplicate',
                        },
                        {
                            id: 'delete',
                            icon: 'delete',
                            label: 'Delete',
                        },
                    ];
            },
            mainMenuItemSelected(item) {
                switch (item.id) {
                    case 'delete':
                        this.destroy()
                        break;
                    case 'accounting.provider':
                        this.$root.goTo(this.expense.sync_record.related_url);
                }
            },
        },
    }
</script>